// VirtualList.jsx
import React, { useRef, useEffect, useCallback } from 'react';
import { useList } from './ListContext';

export function VirtualList({ 
  renderItem, 
  className = '', 
  loadingComponent = <div className="loader"/>,
  errorComponent = null,
  itemClassName = "list-item",
  itemKey = (item, index) => item._id || index,
  onItemClick = null,
  clickable = true
}) {
  const { items, loading, hasMore, fetchMoreItems, error, autoLoadThreshold } = useList();
  const listRef = useRef(null);
  const observerRef = useRef(null);
  
  const lastItemRef = useCallback(node => {
    if (loading) return;
    
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        fetchMoreItems();
      }
    });
    
    if (node) {
      observerRef.current.observe(node);
    }
  }, [loading, hasMore, fetchMoreItems]);

  useEffect(() => {
    const handleScroll = () => {
      if (!listRef.current || !hasMore || loading) return;
      
      const listBottom = listRef.current.getBoundingClientRect().bottom;
      const windowBottom = window.innerHeight;
      
      if (windowBottom + autoLoadThreshold >= listBottom) {
        fetchMoreItems();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [fetchMoreItems, hasMore, loading, autoLoadThreshold]);

  // Handle item click
  const handleItemClick = (item) => {
    if (onItemClick && clickable) {
      onItemClick(item);
    }
  };

  return (
    <div ref={listRef} className={className}>
      {items.map((item, index) => (
        <div 
          key={itemKey(item, index)} 
          className={itemClassName}
          ref={index === items.length - 1 ? lastItemRef : null}
          onClick={() => handleItemClick(item)}
          style={clickable ? { cursor: "pointer" } : undefined}
        >
          {renderItem(item, index)}
        </div>
      ))}
      
      {loading && loadingComponent}
      {error && errorComponent}
    </div>
  );
}